import { getBackendUrl } from "@/lib/backend";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Sparkles, Ticket, Check, ShieldCheck } from "lucide-react";
import { formatINR, loadRazorpay } from "@/lib/razorpay";
import { formatApiError } from "@/lib/apiError";
import { trackEvent } from "@/lib/analytics";

const API = `${getBackendUrl()}/api`;

/**
 * CheckoutDialog — coupon entry + live quote + Razorpay Checkout modal.
 * On payment success, calls /api/billing/verify and refreshes wallet via onPaid().
 */
export default function CheckoutDialog({ open, onOpenChange, pack, onPaid }) {
  const [code, setCode] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchQuote = useCallback(async (couponCode) => {
    if (!pack) return;
    setQuoting(true);
    try {
      const r = await axios.post(`${API}/billing/quote`, {
        pack_id: pack.id,
        coupon_code: couponCode || undefined,
      });
      setQuote(r.data);
      return r.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (couponCode) toast.error(String(detail || "Invalid code for this pack."));
      else toast.error("Could not load pricing.");
      return null;
    } finally {
      setQuoting(false);
    }
  }, [pack]);

  useEffect(() => {
    if (!open) { setCode(""); setQuote(null); return; }
    if (pack) fetchQuote();
  }, [open, pack, fetchQuote]);

  const applyCode = async () => {
    const c = code.trim();
    if (!c) return;
    const data = await fetchQuote(c);
    if (data?.coupon) toast.success(`${c} applied · ${data.coupon.percent}% off`);
  };

  const clearCode = () => {
    setCode("");
    fetchQuote(null);
  };

  const checkout = async () => {
    if (!pack) return;
    setSubmitting(true);
    try {
      // 1) Backend creates the order
      const r = await axios.post(`${API}/billing/order`, {
        pack_id: pack.id,
        coupon_code: quote?.coupon?.code || code || undefined,
      }, { withCredentials: true });
      const order = r.data;

      // 2) Load Razorpay Checkout
      await loadRazorpay();
      if (!window.Razorpay) throw new Error("Razorpay not available.");

      // 3) Close our Radix Dialog BEFORE opening Razorpay.
      //    Radix's modal Dialog locks `pointer-events` on its siblings
      //    (focus-trap behaviour), which prevents the Razorpay iframe
      //    from receiving taps / keyboard input on mobile. Closing our
      //    dialog first hands the interaction layer to Razorpay cleanly.
      onOpenChange(false);
      // Give the browser a paint frame to release focus/aria-hidden locks
      // before mounting the Razorpay portal.
      await new Promise((r2) => setTimeout(r2, 50));

      // 4) Open Razorpay modal
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: order.name,
        description: order.description,
        prefill: order.prefill,
        notes: order.notes,
        // Razorpay sets a cross-merchant cookie on checkout.razorpay.com that
        // remembers the last contact number used from this browser. Without
        // this flag, a different signed-in user can see the previous user's
        // phone ("Using as +91 …") in our checkout. Disable that lookup so
        // the contact field is always blank for the current session.
        remember_customer: false,
        theme: { color: "#D4AF37" },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast.message("Checkout closed. No charge was made.");
          },
        },
        handler: async (resp) => {
          // Backend verifies signature + credits wallet
          try {
            const v = await axios.post(`${API}/billing/verify`, {
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            if (v.data?.ok) {
              toast.success(`Payment confirmed · ${v.data.credits} credits added.`);
              // Funnel bottom — one confirmed purchase event. GA4 also
              // reads this as a monetary conversion if the property is
              // configured with `currency` + `value`, so we ship both.
              trackEvent("credits_purchased", {
                pack: pack?.id || "unknown",
                pack_name: pack?.name || pack?.title || "",
                credits: v.data.credits,
                amount_inr: (order.amount || 0) / 100,
                currency: "INR",
                value: (order.amount || 0) / 100,
                transaction_id: resp.razorpay_payment_id,
              });
              onPaid?.();
            } else {
              toast.error("Verification failed. Please contact support with payment id.");
            }
          } catch (err) {
            toast.error(formatApiError(err, "Verification failed."));
          } finally {
            setSubmitting(false);
          }
        },
      });
      rzp.on("payment.failed", (e) => {
        setSubmitting(false);
        toast.error(`Payment failed: ${e?.error?.description || "Please try again."}`);
      });
      rzp.open();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Please sign in to purchase credits.");
        window.location.href = "/signin";
        return;
      }
      toast.error(formatApiError(err, "Could not start checkout."));
      setSubmitting(false);
    }
  };

  if (!pack) return null;
  const finalAmount = quote?.final_amount ?? pack.amount;
  const discount = quote?.discount ?? 0;
  const couponApplied = quote?.coupon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent
        data-testid="checkout-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white p-0 rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] w-[94vw] max-w-[480px] sm:!rounded-2xl"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/8 space-y-1">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Sparkles className="w-3 h-3" /> Checkout · {pack.credits} credits
          </div>
          <DialogTitle className="font-display text-lg md:text-xl tracking-tight text-white">
            {pack.name} pack
          </DialogTitle>
          <DialogDescription className="text-white/50 text-[11px] leading-snug">
            {pack.subtitle || "One-time purchase · credits never expire"}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">
          {/* Pricing summary */}
          <div className="rounded-xl border border-white/10 bg-[#141414] p-4">
            <div className="flex items-center justify-between text-[12px] text-white/55">
              <span>Pack price</span>
              <span>{formatINR(pack.amount)}</span>
            </div>
            {couponApplied && (
              <div className="flex items-center justify-between text-[12px] text-emerald-400 mt-1.5">
                <span>{couponApplied.code} · {couponApplied.percent}% off</span>
                <span>− {formatINR(discount)}</span>
              </div>
            )}
            <div className="border-t border-white/10 mt-3 pt-3 flex items-baseline justify-between">
              <span className="text-[11px] uppercase tracking-[0.18em] text-white/45">Total</span>
              <div className="font-display text-3xl text-white">
                {quoting ? <Loader2 className="w-5 h-5 animate-spin inline text-[#D4AF37]" /> : formatINR(finalAmount)}
              </div>
            </div>
          </div>

          {/* Coupon input — user can enter any discount code manually.
              No sample/example placeholder per product decision
              (2026-06-10) so we don't accidentally suggest a code
              that may or may not exist. */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Discount code</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                placeholder=""
                maxLength={40}
                disabled={!!couponApplied}
                data-testid="checkout-coupon-input"
                className="flex-1 bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white tracking-[0.12em] uppercase placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition disabled:opacity-50"
              />
              {couponApplied ? (
                <button
                  type="button"
                  onClick={clearCode}
                  data-testid="checkout-coupon-clear"
                  className="text-[11px] px-3 rounded-md border border-white/15 text-white/70 hover:bg-white/5 transition"
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  onClick={applyCode}
                  disabled={quoting || !code.trim()}
                  data-testid="checkout-coupon-apply"
                  className="text-[11px] px-3 rounded-md bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25 transition disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Ticket className="w-3 h-3" /> Apply
                </button>
              )}
            </div>
            {couponApplied && (
              <p className="text-[10px] text-emerald-400 mt-1.5 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Code applied
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={checkout}
              disabled={submitting || quoting}
              data-testid="checkout-confirm"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-xs font-medium py-2.5 hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening Razorpay…</>
              ) : (
                <>Pay {formatINR(finalAmount)} · UPI / Card / NetBanking</>
              )}
            </button>
          </div>

          <p className="text-[10px] text-white/40 leading-snug inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3 text-emerald-400/80" />
            Secure payment by Razorpay · UPI, cards, NetBanking, wallets supported. Credits land in your wallet instantly on success.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
